import { Unarchive } from "@mui/icons-material";
import { Button, CircularProgress, Stack, SxProps, Theme, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import Modal from "../../../App/Components/Modal/Modal";
import { usePaginationState } from "../../../App/Components/Table/State/PaginationState";
import { useArchivedGroupsQuery, useUnarchiveGroupsMutation } from "../../../App/Queries/GroupQueries";
import { createPaginationQueryString } from "../../../App/Queries/queryHelpers";
import { useNotification } from "../../../App/State/Hooks/notificationHooks";
import { useDebouncedHandler } from "../../../App/State/Hooks/useDebouncedHandler";
import { IArchivedGroup } from "../../../App/Typings/Groups";
import { IPaginationQuery } from "../../../App/Typings/MessageText";
import ArchivedGroupsTable from "./ArchivedGroupsTable";

export const ViewArchivedGroups = ({
  isArchivedGroupsOpen,
  setIsArchivedGroupsOpen,
  sx,
}: {
  isArchivedGroupsOpen: boolean;
  setIsArchivedGroupsOpen: (isOpen: boolean) => void;
  sx?: SxProps<Theme>;
}) => {
  const { displaySuccess, displayError } = useNotification();
  const paginationState = usePaginationState(
    { sortBy: "archivedDate", sortDirection: "desc" },
    "archived-groups",
  );
  const paginationQuery = useMemo(
    () =>
      ({
        page: paginationState.getters.page + 1,
        pageSize: paginationState.getters.pageSize,
        sortBy: paginationState.getters.sortBy,
        sortDirection: paginationState.getters.sortDirection,
      }) as IPaginationQuery,
    [
      paginationState.getters.page,
      paginationState.getters.pageSize,
      paginationState.getters.sortBy,
      paginationState.getters.sortDirection,
    ],
  );
  const { data: archivedGroups, isLoading } = useArchivedGroupsQuery(
    createPaginationQueryString(paginationQuery),
    isArchivedGroupsOpen,
  );
  const { mutateAsync: unarchiveGroups, isLoading: isUnarchiveLoading } = useUnarchiveGroupsMutation();
  const [selectedGroups, setSelectedGroups] = useState<IArchivedGroup[]>([]);
  const isAllSelected = selectedGroups.length > 0 && selectedGroups.length === archivedGroups?.items.length;

  const openModal = () => {
    setIsArchivedGroupsOpen(true);
  };
  const closeModal = () => {
    setIsArchivedGroupsOpen(false);
    setSelectedGroups([]);
  };

  const handleSelectGroup = useCallback(
    (row: IArchivedGroup, selected: boolean) => {
      if (selected || !selectedGroups.some((g) => g.id === row.id)) {
        setSelectedGroups([...selectedGroups, row]);
      } else {
        setSelectedGroups(selectedGroups.filter((g) => g.id !== row.id));
      }
    },
    [selectedGroups],
  );

  const handleSelectAllGroups = useCallback(() => {
    if (selectedGroups.length === archivedGroups?.items.length) {
      setSelectedGroups([]);
    } else {
      setSelectedGroups(archivedGroups?.items ?? []);
    }
  }, [selectedGroups, archivedGroups]);

  const isRowSelected = useCallback(
    (row: IArchivedGroup) => selectedGroups.some((g) => g.id === row.id),
    [selectedGroups],
  );

  const handleUnarchiveGroups = useDebouncedHandler(
    async (selectedGroups: IArchivedGroup[], groupId?: string) => {
      const selectedGroupIds = selectedGroups.map((g) => g.id);
      const isSingle = !!groupId || selectedGroupIds.length === 1;
      await unarchiveGroups(groupId ? [groupId] : selectedGroupIds, {
        onSuccess: () => {
          setSelectedGroups([]);
          displaySuccess(`Group${isSingle ? "" : "s"} unarchived successfully`);
        },
        onError: () => {
          displayError(`Error unarchiving group${isSingle ? "" : "s"}`);
        },
      });
    },
    300,
  );

  return (
    <>
      <Button
        size="small"
        color="inherit"
        variant="outlined"
        onClick={openModal}
        data-test="view-archived-groups"
        sx={sx}
      >
        View Archived Groups
      </Button>
      <Modal
        size="lg"
        title="Archived Groups"
        dataTestId="archived-groups"
        isOpen={isArchivedGroupsOpen}
        handleCloseModal={closeModal}
        footerContent={
          <Button
            data-test="unarchive-groups"
            variant="contained"
            size="small"
            color="primary"
            disabled={isUnarchiveLoading || selectedGroups.length === 0}
            onClick={() => handleUnarchiveGroups(selectedGroups)}
            startIcon={<Unarchive />}
          >
            Unarchive Selected ({selectedGroups.length})
          </Button>
        }
        content={
          isLoading ? (
            <Stack sx={{ width: "100%", justifyContent: "center", alignItems: "center", p: 2 }}>
              <CircularProgress />
              <Typography>Loading...</Typography>
            </Stack>
          ) : (
            <ArchivedGroupsTable
              pagination={{
                rowsPerPage: [10, 15, 25],
                rows: paginationState.getters.pageSize,
                currentPage: paginationState.getters.page,
                handleChangePage: paginationState.handlers.handleChangePage,
                handleChangeRowsPerPage: paginationState.handlers.handleChangePageSize,
                enabled: !isLoading,
                totalCount: archivedGroups?.totalItems ?? -1,
              }}
              groups={archivedGroups?.items ?? []}
              isLoading={isLoading}
              isUnarchiveLoading={isUnarchiveLoading}
              onGroupUnarchive={(groupId) => handleUnarchiveGroups(selectedGroups, groupId)}
              onRowClick={(row) => handleSelectGroup(row, !isRowSelected(row))}
              sortBy={paginationState.getters.sortBy}
              sort={paginationState.getters.sortDirection}
              onSort={paginationState.handlers.handleRequestSort}
              onRowSelect={handleSelectGroup}
              isRowSelected={isRowSelected}
              onAllSelected={handleSelectAllGroups}
              isAllSelected={isAllSelected}
            />
          )
        }
      />
    </>
  );
};
